import { useEffect, useMemo, useRef, useState } from "react";

function filterToken(key, value) {
  return `${key}:${value}`;
}

export default function VariantFilterBar({
  groups = [],
  activeFilters = [],
  onToggleFilter,
  onRemoveFilter,
  onClear,
  visibleCount,
  totalCount,
  integrated = false,
}) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);

  const activeTokens = useMemo(
    () => new Set(activeFilters.map((f) => filterToken(f.key, f.value))),
    [activeFilters]
  );

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div
      className={
        integrated
          ? "border-b border-zinc-200 bg-zinc-50/70 px-3 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/30"
          : "rounded-xl border border-zinc-200 bg-zinc-50/70 px-3 py-2.5 shadow-sm dark:border-zinc-800 dark:bg-[#121214]"
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400">
          Filters
        </span>
        {activeFilters.map((chip) => (
          <button
            key={filterToken(chip.key, chip.value)}
            type="button"
            onClick={() => onRemoveFilter(chip.key, chip.value)}
            className="inline-flex items-center gap-1 rounded-full border border-zinc-300 bg-zinc-50 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-100
                       dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
            title="Remove filter"
          >
            <span>{chip.key}: {chip.value}</span>
            <span aria-hidden="true">x</span>
          </button>
        ))}

        <div className="relative" ref={popoverRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center rounded-full border border-zinc-300 bg-white px-3 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-50
                       dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            + Add Filter
          </button>

          {open && (
            <div className="absolute left-1/2 top-10 z-30 w-[calc(100vw-2rem)] max-w-[20rem] -translate-x-1/2 rounded-xl border border-zinc-200 bg-white p-3 shadow-xl sm:left-0 sm:w-[320px] sm:max-w-none sm:translate-x-0 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="mb-2 text-xs font-semibold text-zinc-900 dark:text-zinc-100">Filter variants</div>

              <div className="max-h-[min(22rem,calc(100dvh-9rem))] space-y-3 overflow-auto pr-1">
                {groups.map((group) => (
                  <div key={group.name}>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                      {group.name}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {group.values.map((value) => {
                        const token = filterToken(group.name, value);
                        const active = activeTokens.has(token);
                        return (
                          <button
                            key={token}
                            type="button"
                            onClick={() => onToggleFilter(group.name, value)}
                            className={
                              "rounded-full border px-2 py-1 text-[11px] font-medium " +
                              (active
                                ? "border-zinc-900 bg-zinc-900 text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900"
                                : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800")
                            }
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {activeFilters.length > 0 && (
                <button
                  type="button"
                  onClick={onClear}
                  className="mt-3 text-xs font-medium text-zinc-500 underline dark:text-zinc-400"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        <div className="ml-auto rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
          Showing {visibleCount} of {totalCount} variants
        </div>
      </div>
    </div>
  );
}
